// packages/frontend/src/components/admin/NamedRegionsList.tsx
import { useEffect, useMemo, useState } from 'react';
import { Edit2, Trash2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNamedRegionsStore } from '../../store/namedRegionsStore';
import type { NamedRegion } from '../../types/namedRegion';
import { NamedRegionEditDialog } from './NamedRegionEditDialog';
import { ConfirmDialog } from '../common/ConfirmDialog';

export function NamedRegionsList() {
  const { regions, loading, fetchRegions, deleteRegion } = useNamedRegionsStore();
  const [editing, setEditing] = useState<NamedRegion | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<NamedRegion | null>(null);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  const sortedRegions = useMemo(() => {
    return [...regions].sort((a, b) => {
      const byState = a.stateCode.localeCompare(b.stateCode);
      if (byState !== 0) return byState;
      return a.name.localeCompare(b.name);
    });
  }, [regions]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteRegion(pendingDelete.id);
      toast.success(`Deleted ${pendingDelete.name}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setPendingDelete(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Sport Season Regions
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            ZIP-3 prefix groupings used for per-region sport-season overrides.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="btn-primary inline-flex items-center gap-1"
        >
          <Plus size={16} /> Add Region
        </button>
      </div>

      {loading && regions.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 p-4">Loading…</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left px-3 py-2 w-24">State</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">ZIP-3 prefixes</th>
                <th className="text-right px-3 py-2 w-40">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-900 dark:text-gray-100">
              {sortedRegions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-10 text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-3">
                      No regions defined yet.
                    </p>
                    <button
                      type="button"
                      className="btn-primary inline-flex items-center gap-1"
                      onClick={() => setCreating(true)}
                    >
                      <Plus size={16} /> Add your first region
                    </button>
                  </td>
                </tr>
              ) : (
                sortedRegions.map((r) => {
                  const zips = r.zip3s.map((z) => z.zip3).sort();
                  const zipsDisplay = zips.join(', ');
                  return (
                    <tr
                      key={r.id}
                      className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-3 py-2">
                        <span className="inline-block px-1.5 py-0.5 rounded text-xs font-mono bg-gray-100 dark:bg-gray-700">
                          {r.stateCode}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td
                        className="px-3 py-2 text-gray-700 dark:text-gray-300 truncate max-w-md"
                        title={zipsDisplay}
                      >
                        {zipsDisplay}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setEditing(r)}
                          className="text-blast-600 dark:text-blast-400 hover:underline mr-3 inline-flex items-center gap-1"
                        >
                          <Edit2 size={14} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(r)}
                          className="text-red-600 dark:text-red-400 hover:underline inline-flex items-center gap-1"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {(editing || creating) && (
        <NamedRegionEditDialog
          region={editing ?? undefined}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete region?"
        message={
          pendingDelete
            ? `Delete region "${pendingDelete.name}"? This won't change sport defaults that reference these ZIP-3s — only the named grouping is removed.`
            : ''
        }
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
